import { array, nonEmptyArray, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { DateTimeUtc, DateTimeUtcService } from '@mews/framework';
import { getServiceAvailabilityHash } from '../../modules/serviceAvailability/utils';

import { getDateIsoStringFromDateTimeUtcString } from '../../utils/dates';
import { getAvailabilityHash } from '../../utils/getAvailabilityHash';

interface RequestedReservation {
    roomCategoryId: string;
    startUtc: string;
    endUtc: string;
    voucherCode?: string;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

interface GetExceedingReservationCountsOptions {
    requestedReservations: nonEmptyArray.NonEmptyArray<RequestedReservation>;
    exceedingReservationIndexes: number[];
    timezone: string;
    enterpriseId: string;
    serviceId: string;
}

interface ReservationExceedingCountData {
    roomCategoryId: string;
    availabilityHash: string;
    serviceAvailabilityHash: string;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

const groupByRoomCategoryAvailabilityHash = (reservationExceedingCountData: ReservationExceedingCountData[]) =>
    pipe(
        reservationExceedingCountData,
        nonEmptyArray.groupBy(({ roomCategoryId, availabilityHash }) => `${roomCategoryId}-${availabilityHash}`)
    );

const getRequestedReservationsCountData = ({
    requestedReservations,
    timezone,
    enterpriseId,
    serviceId,
}: Pick<GetExceedingReservationCountsOptions, 'enterpriseId' | 'timezone' | 'requestedReservations' | 'serviceId'>) =>
    pipe(
        requestedReservations,
        nonEmptyArray.map(
            ({
                startUtc: startUtcIsoString,
                endUtc: endUtcIsoString,
                voucherCode,
                roomCategoryId,
                startTimeUtc,
                endTimeUtc,
            }) => {
                const availabilityHash = getAvailabilityHash({
                    startDate: startTimeUtc ?? getDateIsoStringFromDateTimeUtcString(startUtcIsoString, { timezone }),
                    endDate: endTimeUtc ?? getDateIsoStringFromDateTimeUtcString(endUtcIsoString, { timezone }),
                    enterpriseId,
                    voucherCode,
                });

                return {
                    availabilityHash,
                    roomCategoryId,
                    serviceAvailabilityHash: getServiceAvailabilityHash({ categoryId: roomCategoryId, serviceId }),
                    startUtc: DateTimeUtcService.parse(startUtcIsoString),
                    endUtc: DateTimeUtcService.parse(endUtcIsoString),
                    startTimeUtc,
                    endTimeUtc,
                };
            }
        ),
        groupByRoomCategoryAvailabilityHash
    );

const getExceedingReservationsCount = ({
    exceedingReservationIndexes,
    requestedReservations,
    timezone,
    enterpriseId,
    serviceId,
}: GetExceedingReservationCountsOptions) =>
    pipe(
        exceedingReservationIndexes,
        array.map((exceedingIndex) => {
            const exceedingReservationOption = option.fromNullable(requestedReservations[exceedingIndex]);
            return pipe(
                exceedingReservationOption,
                option.map(
                    ({
                        startUtc: startUtcIsoString,
                        endUtc: endUtcIsoString,
                        roomCategoryId,
                        voucherCode,
                        startTimeUtc,
                        endTimeUtc,
                    }) => ({
                        availabilityHash: getAvailabilityHash({
                            startDate:
                                startTimeUtc ?? getDateIsoStringFromDateTimeUtcString(startUtcIsoString, { timezone }),
                            endDate: endTimeUtc ?? getDateIsoStringFromDateTimeUtcString(endUtcIsoString, { timezone }),
                            enterpriseId,
                            voucherCode,
                        }),
                        serviceAvailabilityHash: getServiceAvailabilityHash({ categoryId: roomCategoryId, serviceId }),
                        startUtc: DateTimeUtcService.parse(startUtcIsoString),
                        endUtc: DateTimeUtcService.parse(endUtcIsoString),
                        roomCategoryId,
                        startTimeUtc,
                        endTimeUtc,
                    })
                )
            );
        }),
        array.compact,
        groupByRoomCategoryAvailabilityHash
    );

export const getExceedingReservationsData = ({
    requestedReservations,
    exceedingReservationIndexes,
    timezone,
    enterpriseId,
    serviceId,
}: GetExceedingReservationCountsOptions) => {
    const requestedReservationsCountData = getRequestedReservationsCountData({
        requestedReservations,
        enterpriseId,
        timezone,
        serviceId,
    });
    const exceedingReservationsCountData = getExceedingReservationsCount({
        exceedingReservationIndexes,
        requestedReservations,
        enterpriseId,
        timezone,
        serviceId,
    });

    return pipe(
        exceedingReservationsCountData,
        record.mapWithIndex((hash, exceedingReservationsData) => {
            const requestedCountOption = pipe(requestedReservationsCountData, record.lookup(hash));
            const {
                roomCategoryId,
                availabilityHash,
                serviceAvailabilityHash,
                startUtc,
                endUtc,
                startTimeUtc,
                endTimeUtc,
            } = pipe(exceedingReservationsData, nonEmptyArray.head);

            return pipe(
                requestedCountOption,
                option.map((requestedCount) => ({
                    availabilityHash,
                    serviceAvailabilityHash,
                    startUtc,
                    endUtc,
                    roomCategoryId,
                    requestedCount: requestedCount.length,
                    exceedingCount: exceedingReservationsData.length,
                    ...(startTimeUtc != null && { startTimeUtc }),
                    ...(endTimeUtc != null && { endTimeUtc }),
                }))
            );
        }),
        record.compact,
        record.collect((_hash, value) => value)
    );
};
